import moment from 'moment';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components';
import { classNames } from '../../utils';
import { NotificationType } from './types';

const dateFormat = 'YYYY/MM/DD';
interface NotificationTemplateProp {
  notifications: NotificationType[];
  onUpdate: (notificationId: string, isRead: boolean) => void;
  buttonText: string;
  buttonClassname?: string;
  className?: string;
  hasUnreadNotifications?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  preformanceConfig?: Record<string, any>;
}

export const NotificationTemplate: FC<NotificationTemplateProp> = ({
  notifications,
  onUpdate,
  buttonText,
  buttonClassname = '',
  className = '',
  preformanceConfig,
}) => {
  const { t } = useTranslation();

  return (
    <div className={classNames('flex flex-col gap-4', className)}>
      {notifications.map((item, index) => {
        const { id, createdDateTime: dateTime, type, isRead, showClearButton } = item;
        let lastElemRefOption = { ref: preformanceConfig?.isItemVisible };
        if (notifications.length === index + 1) {
          lastElemRefOption = { ref: preformanceConfig?.lastItemRef };
        }

        if (type === 'HEADER') {
          return (
            <div
              key={`header-${index}`}
              className="bg-white dark:bg-slate-800 rounded-2xl px-5 py-4 flex items-center shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700 mt-4 first:mt-0"
            >
              <div className="flex-1">
                <span className="text-lg font-semibold text-orange dark:text-teal-400">{item.summary}</span>
              </div>
              {showClearButton && (
                <Button
                  className="px-4 py-2 text-sm font-medium rounded-full bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-md shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-lg transition-all"
                  onClick={() => onUpdate('', true)}
                  props={{
                    name: 'clearAll',
                  }}
                >
                  {t('notifications:clear_all', 'Clear All')}
                </Button>
              )}
            </div>
          );
        }

        const body = t(`notifications:messageTypes.${type.toLowerCase()}.body`);
        const subject = t(`notifications:messageTypes.${type.toLowerCase()}.subject`);
        return (
          <div
            {...lastElemRefOption}
            className={`${
              preformanceConfig?.fadeOnScrollClassName
            } flex flex-col bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700 ${
              !isRead ? 'border-l-4 border-l-orange dark:border-l-teal-500' : ''
            } transition-all hover:shadow-md dark:hover:shadow-slate-900/70`}
            key={`${id}-${index}`}
          >
            <div className="flex w-full items-center mb-3">
              <div className="flex items-center gap-2 flex-1">
                {!isRead && <span className="w-2 h-2 rounded-full bg-orange dark:bg-teal-500 flex-shrink-0" />}
                <span
                  className={`text-xs font-medium ${
                    isRead ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {moment(dateTime).format(dateFormat)}
                </span>
              </div>
              {!isRead && (
                <Button
                  isLink={true}
                  className={`text-sm font-medium ${buttonClassname} transition-colors`}
                  onClick={() => onUpdate(id, true)}
                  props={{
                    name: id,
                  }}
                >
                  {buttonText}
                </Button>
              )}
            </div>
            <div className="flex flex-col w-full space-y-2">
              <span
                className={`w-full font-semibold text-base ${
                  isRead ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'
                }`}
              >
                {subject}
              </span>
              <p
                className={`text-sm leading-relaxed ${
                  isRead ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                {body}
              </p>
              <div className="flex w-full pt-3 border-t border-gray-100 dark:border-slate-700 mt-2">
                <span
                  className={`text-xs ${
                    isRead ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {moment(dateTime).fromNow()}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
